import {
  getFunctionsByCategory,
  type FunctionDefinition,
} from '../services/functionService';
import type { AgentInlineSelectOption } from '../components/ai-agent-ui/index';

export type AgentCustomFunctionDto = {
  id: number;
  name: string;
  type?: string;
  code?: string | null;
  value?: string | null;
  version?: number;
  isActive?: boolean;
};

export type AgentFunctionPickOption = AgentInlineSelectOption & {
  name: string;
  category: string;
  syntax?: string;
  example?: string;
  description?: string;
  source: 'custom' | 'builtin';
};

/** Normalize user/agent function names to the `$name` form used by the runtime. */
export function normalizeAgentFunctionName(raw: string): string {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';
  return trimmed.startsWith('$') ? trimmed : `$${trimmed}`;
}

export function stripAgentFunctionDollar(raw: string): string {
  const trimmed = (raw || '').trim();
  return trimmed.startsWith('$') ? trimmed.slice(1) : trimmed;
}

export function formatAgentFunctionResult(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function buildAgentFunctionPickOptions(
  customFunctions: AgentCustomFunctionDto[],
  search = '',
): AgentFunctionPickOption[] {
  const q = search.trim().toLowerCase();
  const options: AgentFunctionPickOption[] = [];

  for (const fn of customFunctions) {
    if (!fn?.name || fn.isActive === false) continue;
    const name = normalizeAgentFunctionName(fn.name);
    const category = 'Custom';
    const label = `${name} · Custom`;
    const hay = `${name} ${fn.name} custom ${fn.type || ''}`.toLowerCase();
    if (q && !hay.includes(q)) continue;
    options.push({
      value: name,
      label,
      name,
      category,
      syntax: name,
      description: `Custom ${(fn.type || 'JS').toUpperCase()} function`,
      source: 'custom',
    });
  }

  const byCategory = getFunctionsByCategory();
  for (const [category, defs] of Object.entries(byCategory)) {
    if (category === 'Custom') continue;
    for (const def of defs as FunctionDefinition[]) {
      const name = def.name;
      const hay = `${name} ${def.description || ''} ${def.syntax || ''} ${category}`.toLowerCase();
      if (q && !hay.includes(q)) continue;
      options.push({
        value: name,
        label: `${name} · ${category}`,
        name,
        category,
        syntax: def.syntax,
        example: def.example,
        description: def.description,
        source: 'builtin',
      });
    }
  }

  return options.sort((a, b) => {
    if (a.source !== b.source) return a.source === 'custom' ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

export function buildStaticFunctionValuePayload(
  valueType: 'STRING' | 'NUMBER' | 'BOOLEAN' | 'JSON',
  raw: string,
): string {
  if (valueType === 'STRING') {
    return JSON.stringify(raw);
  }
  if (valueType === 'NUMBER') {
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      throw new Error('Enter a valid number');
    }
    return n.toString();
  }
  if (valueType === 'BOOLEAN') {
    return raw === 'true' ? 'true' : 'false';
  }
  const t = raw.trim();
  JSON.parse(t);
  return t;
}
